import React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { trailingSlashIt } from "@bufox/js-utils"

function fixedEncodeURIComponent(str) {
  return encodeURIComponent(str).replace(/[!'()*]/g, c => {
    return `%${c.charCodeAt(0).toString(16)}`
  })
}

/* eslint-disable-next-line complexity*/
function WpSeoHelmet(props) {
  const {
    enableGoogleTcfSupport,
    enableOpenGraph,
    enableTwitter,
    excerpt,
    language,
    publishDate,
    seo,
    siteUrl,
    title,
    translations = [],
  } = props
  const meta = []
  const { metaDesc, metaRobotsNofollow, metaRobotsNoindex } = seo

  const robotsArray = [metaRobotsNoindex, metaRobotsNofollow]

  meta.push({
    name: `robots`,
    content: robotsArray.join(`, `),
  })

  meta.push({
    name: `description`,
    content: metaDesc
      ? metaDesc
      : excerpt
      ? excerpt.replace(/<[^>]*>?|\r\n|\n|\r/gm, "")
      : "", // Removing html and line breaks from excerpt
  })

  /**
   * Open Graph support: https://ogp.me/
   *
   * Required Fields:
   *
   * og:title - The title of your object as it should appear
   *    within the graph, e.g., "The Rock".
   * og:type - The type of your object, e.g., "video.movie".
   *    Depending on the type you specify, other properties may
   *    also be required.
   * og:image - An image URL which should represent your
   *    object within the graph.
   * og:url - The canonical URL of your object that will be used as
   *    its permanent ID in the graph, e.g., "https://www.imdb.com/title/tt0117500/".
   */

  if (
    enableOpenGraph &&
    (seo.opengraphTitle || title) &&
    seo.opengraphType &&
    seo.opengraphUrl
  ) {
    meta.push({
      property: `og:title`,
      content: seo.opengraphTitle || title,
    })

    meta.push({
      property: `og:type`,
      content: seo.opengraphType,
    })

    meta.push({
      property: `og:url`,
      content: `${siteUrl}${seo.opengraphUrl}`,
    })

    // Image section. Use if specified, otherwise use placeholder.com images: https://placeholder.com/
    if (seo.opengraphImage?.localFile?.publicURL) {
      meta.push({
        property: `og:image`,
        content: siteUrl + seo.opengraphImage.localFile.publicURL,
      })
      meta.push({
        property: `og:image:width`,
        content: seo.opengraphImage.mediaDetails.width,
      })
      meta.push({
        property: `og:image:height`,
        content: seo.opengraphImage.mediaDetails.height,
      })
    } else {
      meta.push({
        property: `og:image`,
        content: `https://via.placeholder.com/1200x630.png?text=${fixedEncodeURIComponent(
          seo.title || title
        )}`,
      })
      meta.push({
        property: `og:image:width`,
        content: "1260",
      })
      meta.push({
        property: `og:image:height`,
        content: "630",
      })
    }

    // optional fields

    if (seo.opengraphDescription || excerpt) {
      meta.push({
        property: `og:description`,
        content: seo.opengraphDescription || excerpt,
      })
    }

    if (seo.opengraphAutor) {
      meta.push({
        property: `og:author`,
        content: seo.opengraphAutor,
      })
    }
    if (seo.opengraphType === `article`) {
      if (seo.opengraphModifiedTime) {
        meta.push({
          property: `article:modified_time`,
          content: seo.opengraphModifiedTime,
        })
      }
      if (seo.opengraphPublishedTime || publishDate) {
        meta.push({
          property: `article:published_time`,
          content: seo.opengraphPublishedTime || publishDate,
        })
      }
    }

    if (seo.opengraphSiteName) {
      meta.push({
        property: `og:site_name`,
        content: seo.opengraphSiteName,
      })
    }

    if (language?.locale) {
      meta.push({
        property: `og:locale`,
        content: language.locale,
      })
    }
  }

  /**
   * Twitter tags
   *
   * https://developer.twitter.com/en/docs/twitter-for-websites/cards/overview/summary
   *
   * Note that some properties will fall back to og
   * https://developer.twitter.com/en/docs/twitter-for-websites/cards/overview/markup
   */

  if (enableTwitter) {
    meta.push({
      name: `twitter:card`,
      content: `summary`,
    })
    if (seo.twitterTitle || title) {
      meta.push({
        name: `twitter:title`,
        content: seo.twitterTitle || title,
      })
    }
    if (seo.twitterDescription || excerpt) {
      meta.push({
        name: `twitter:description`,
        content: seo.twitterDescription || excerpt,
      })
    }

    if (seo.twitterImage?.localFile?.publicURL) {
      meta.push({
        name: `twitter:image`,
        content: seo.twitterImage.localFile.publicURL,
      })
    }
  }

  return (
    <Helmet
      htmlAttributes={{
        lang: language?.code ? language.code : null,
      }}
      title={seo.title}
      meta={meta}
    >
      {seo.canonical && siteUrl ? (
        <link rel="canonical" href={`${siteUrl}${seo.canonical}`} />
      ) : (
        ``
      )}
      {enableGoogleTcfSupport && (
        <script type="application/javascript">
          {`window['gtag_enable_tcf_support'] = true`}
        </script>
      )}
      {translations.map((translation, i) => {
        return translation.language?.locale && translation.uri ? (
          <link
            key={`translation-${i}`}
            rel="alternate"
            hrefLang={translation.language.locale}
            href={trailingSlashIt(siteUrl) + trailingSlashIt(translation.uri)}
          />
        ) : null
      })}
    </Helmet>
  )
}

WpSeoHelmet.defaultProps = {
  meta: [],
  description: ``,
  enableGoogleTcfSupport: true,
  enableOpenGraph: true,
  enableTwitter: true,
}

WpSeoHelmet.propTypes = {
  enableGoogleTcfSupport: PropTypes.bool,
  enableOpenGraph: PropTypes.bool,
  enableTwitter: PropTypes.bool,
  excerpt: PropTypes.string,
  language: PropTypes.object.isRequired,
  meta: PropTypes.arrayOf(PropTypes.object),
  publishDate: PropTypes.string,
  seo: PropTypes.shape({
    canonical: PropTypes.string,
    title: PropTypes.string,
    metaDesc: PropTypes.string,
    metaRobotsNofollow: PropTypes.string,
    metaRobotsNoindex: PropTypes.string,
    opengraphImage: PropTypes.object,
    opengraphTitle: PropTypes.string,
    opengraphType: PropTypes.string,
    opengraphUrl: PropTypes.string,
    opengraphDescription: PropTypes.string,
    opengraphAutor: PropTypes.string,
    opengraphModifiedTime: PropTypes.string,
    opengraphPublishedTime: PropTypes.string,
    opengraphSiteName: PropTypes.string,
    translations: PropTypes.array,
    twitterTitle: PropTypes.string,
    twitterDescription: PropTypes.string,
    twitterImage: PropTypes.string,
    url: PropTypes.string,
  }),
  siteUrl: PropTypes.string,
  title: PropTypes.string,
  translations: PropTypes.array,
}

export default WpSeoHelmet
